'use client';

import { Minus, Plus, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/components/ui/utils';

export function PointsRedeemer({
  balance,
  redeemPoints,
  redeemValue,
  redemptionRate,
  onIncrement,
  onDecrement,
  onRedeem,
  loading,
  disabled,
}: {
  balance: number;
  redeemPoints: number;
  redeemValue: number;
  redemptionRate: { pointsPerUnit: number; valueAmount: number };
  onIncrement: () => void;
  onDecrement: () => void;
  onRedeem: () => void;
  loading?: boolean;
  disabled?: boolean;
}) {
  const canDecrement = redeemPoints > 0;
  const canIncrement = redeemPoints + redemptionRate.pointsPerUnit <= balance;

  return (
    <div className="cs-card space-y-4">
      <p className="cs-label">Spend your points</p>
      <p className="cs-body-sm text-[var(--cs-text-secondary)]">Pick an amount, then show the confirmation to staff.</p>

      <div className="flex items-center gap-4 rounded-[var(--cs-radius-lg)] border border-[var(--cs-border)] bg-[var(--cs-surface-muted)] p-4">
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label="Use fewer points"
          className="cs-focusable size-11 shrink-0 rounded-full"
          disabled={!canDecrement}
          onClick={onDecrement}
        >
          <Minus className="size-4" aria-hidden="true" />
        </Button>
        <div className="flex-1 text-center" aria-live="polite">
          <p className="cs-amount cs-amount-md tabular-nums">{redeemPoints}</p>
          <p className="cs-body-sm mt-1 text-[var(--cs-text-secondary)]">
            {redeemPoints > 0 ? `${redeemValue.toFixed(2)} off` : 'points to spend'}
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label="Use more points"
          className="cs-focusable size-11 shrink-0 rounded-full"
          disabled={!canIncrement}
          onClick={onIncrement}
        >
          <Plus className="size-4" aria-hidden="true" />
        </Button>
      </div>

      <Button
        type="button"
        className="cs-focusable min-h-[var(--cs-touch-rec)] w-full"
        disabled={redeemPoints <= 0 || disabled || loading}
        onClick={onRedeem}
      >
        {loading ? <Loader2 className="mr-1.5 size-4 animate-spin" /> : null}
        {loading
          ? 'Redeeming...'
          : redeemPoints > 0
            ? `Redeem ${redeemPoints} points · ${redeemValue.toFixed(2)} off`
            : 'Choose an amount above'}
      </Button>
    </div>
  );
}
